import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Trash2, Download, FileText, RefreshCw, BarChart3, Table, RotateCcw } from 'lucide-react'
import { useDatasetStore } from '@/store/datasetStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { LoadingPage } from '@/components/ui/loading-spinner'
import { showErrorToast, showSuccessToast } from '@/utils/error-handler'
import { useTranslation } from 'react-i18next'
import { datasetsApi } from '@/services/api/datasets'
import type { DatasetPreview, DatasetSyncInfo } from '@/types/dataset'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import DatasetPreviewChart from '@/components/datasets/DatasetPreviewChart'

export default function DatasetDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { currentDataset, loadDataset, deleteDataset, isLoading } = useDatasetStore()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [preview, setPreview] = useState<DatasetPreview | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [previewView, setPreviewView] = useState<'table' | 'chart'>('table')
  const [syncInfo, setSyncInfo] = useState<DatasetSyncInfo | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [selectedSymbol, setSelectedSymbol] = useState<string>('')

  // Handle symbol change for dataset preview
  const handleSymbolChange = (symbol: string) => {
    // Convert "ALL_SYMBOLS" back to empty string for storage
    const storedSymbol = symbol === 'ALL_SYMBOLS' ? '' : symbol
    setSelectedSymbol(storedSymbol)
    // Save symbol selection to localStorage
    if (id) {
      localStorage.setItem(`dataset_preview_symbol_${id}`, storedSymbol)
    }
  }

  useEffect(() => {
    if (id) {
      loadDataset(id)
      loadPreview(id)
      loadSyncInfo(id)

      // Load symbol selection for this dataset
      const savedSymbol = localStorage.getItem(`dataset_preview_symbol_${id}`)
      if (savedSymbol) {
        setSelectedSymbol(savedSymbol)
      }
    }
  }, [id, loadDataset])

  const loadPreview = async (datasetId: string) => {
    setIsLoadingPreview(true)
    try {
      const previewData = await datasetsApi.preview(datasetId)
      setPreview(previewData)
    } catch (error) {
      console.error('Failed to load preview:', error)
    } finally {
      setIsLoadingPreview(false)
    }
  }

  const loadSyncInfo = async (datasetId: string) => {
    try {
      const syncData = await datasetsApi.getSyncStatus(datasetId)
      setSyncInfo(syncData)
    } catch (error) {
      console.error('Failed to load sync info:', error)
      setSyncInfo(null)
    }
  }

  const handleSync = async () => {
    if (!id) return
    setIsSyncing(true)
    try {
      // For now, we'll simulate a sync by reloading the sync info
      // In a real implementation, this would call a sync endpoint
      await new Promise(resolve => setTimeout(resolve, 2000)) // Simulate sync delay
      await loadSyncInfo(id)
      showSuccessToast('Sincronización completada')
    } catch (error) {
      showErrorToast(error, 'Error al sincronizar')
    } finally {
      setIsSyncing(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return
    setIsDeleting(true)
    try {
      await deleteDataset(id)
      showSuccessToast(t('datasets.deleteSuccess'))
      navigate('/datasets')
    } catch (error) {
      showErrorToast(error, t('errors.deleteDataset'))
    } finally {
      setIsDeleting(false)
      setShowDeleteDialog(false)
    }
  }

  const handleRegenerate = async () => {
    if (!id || !currentDataset) return
    setIsRegenerating(true)
    try {
      // Use the current dataset's metadata and column mapping for regeneration
      const metadata = currentDataset.metadata || {}
      const csvMapping = {
        timestampColumn: 'timestamp', // Standard column name after conversion
        timestampUnit: 's', // Default to seconds
        symbol: currentDataset.symbol
      }

      await datasetsApi.regenerate(id, metadata, csvMapping)
      showSuccessToast(t('datasets.regenerateSuccess'))

      // Reload the dataset to show updated information
      await loadDataset(id)
    } catch (error) {
      showErrorToast(error, t('errors.generic'))
    } finally {
      setIsRegenerating(false)
      setShowRegenerateDialog(false)
    }
  }

  if (isLoading) {
    return <LoadingPage message={t('common.loading')} />
  }

  if (!currentDataset) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-lg text-muted-foreground">{t('errors.datasetNotFound')}</p>
          <Button onClick={() => navigate('/datasets')} className="mt-4">
            {t('common.back')}
          </Button>
        </div>
      </div>
    )
  }

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, 'default' | 'secondary' | 'destructive'> = {
      ready: 'default',
      processing: 'secondary',
      failed: 'destructive',
    }
    return (
      <Badge variant={statusMap[status] || 'default'}>
        {t(`datasets.status.${status}`)}
      </Badge>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/datasets')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{currentDataset.name}</h1>
            <p className="text-muted-foreground mt-1">
              {t('datasets.datasetDetails')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => loadPreview(id!)}>
            <FileText className="w-4 h-4 mr-2" />
            {t('datasets.preview')}
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            {t('common.download')}
          </Button>
          {syncInfo?.syncEnabled && (
            <Button variant="outline" onClick={handleSync} disabled={isSyncing} title="Sincronizar datos">
              <RotateCcw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowRegenerateDialog(true)} title={t('datasets.regenerateColumnsDescription')}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('datasets.regenerate')}
          </Button>
          <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
            <Trash2 className="w-4 h-4 mr-2" />
            {t('common.delete')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('datasets.preview')}</CardTitle>
                {preview && preview.rows.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant={previewView === 'table' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPreviewView('table')}
                    >
                      <Table className="w-4 h-4 mr-2" />
                      Table
                    </Button>
                    <Button
                      variant={previewView === 'chart' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPreviewView('chart')}
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Chart
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingPreview ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-muted-foreground">{t('common.loading')}</p>
                </div>
              ) : preview && preview.rows.length > 0 ? (
                previewView === 'table' ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b">
                          {preview.columns.map((col) => (
                            <th key={col} className="text-left p-2 font-semibold">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.slice(0, 10).map((row, idx) => (
                          <tr key={idx} className="border-b">
                            {preview.columns.map((col) => (
                              <td key={col} className="p-2 text-sm">
                                {String(row[col] ?? '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {preview.rows.length > 10 && (
                      <p className="text-sm text-muted-foreground mt-4">
                        {t('datasets.showingPreview', { count: 10, total: preview.rows.length })}
                      </p>
                    )}
                  </div>
                ) : (
                  <DatasetPreviewChart
                    data={preview.rows}
                    columns={preview.columns}
                    isLoading={isLoadingPreview}
                    title="Dataset Chart Preview"
                    isMultiSymbol={currentDataset?.metadata?.partitionStrategy === 'by_symbol' ||
                                    currentDataset?.metadata?.partitionType === 'symbol'}
                    availableSymbols={((currentDataset?.metadata as any)?.symbols as string[]) ||
                                    ((currentDataset?.metadata as any)?.syncMetadata?.symbols as string[]) || []}
                    selectedSymbol={selectedSymbol}
                    onSymbolChange={handleSymbolChange}
                  />
                )
              ) : (
                <p className="text-muted-foreground">{t('common.noData')}</p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('datasets.information')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('common.status')}</p>
                <div className="mt-1">{getStatusBadge(currentDataset.status)}</div>
              </div>
              {currentDataset.symbol && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('datasets.symbol')}</p>
                  <p className="mt-1">{currentDataset.symbol}</p>
                </div>
              )}
              {currentDataset.timeframe && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('datasets.timeframe')}</p>
                  <p className="mt-1">{currentDataset.timeframe}</p>
                </div>
              )}
              {currentDataset.rowCount && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('datasets.rows')}</p>
                  <p className="mt-1">
                    {currentDataset.rowCount != null 
                      ? currentDataset.rowCount.toLocaleString() 
                      : t('datasets.processing') || 'Processing...'}
                  </p>
                </div>
              )}
              {currentDataset.createdAt && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('common.created')}</p>
                  <p className="mt-1">
                    {new Date(currentDataset.createdAt).toLocaleDateString()}
                  </p>
                </div>
              )}
              {syncInfo && syncInfo.syncEnabled && (
                <>
                  {syncInfo.lastSuccessfulSync && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Última sincronización</p>
                      <p className="mt-1">
                        {new Date(syncInfo.lastSuccessfulSync).toLocaleString()}
                      </p>
                    </div>
                  )}
                  {syncInfo.lastSyncRecordsAdded !== undefined && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Registros agregados en último sync</p>
                      <p className="mt-1">
                        {syncInfo.lastSyncRecordsAdded.toLocaleString()}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Comando CLI para sincronizar</p>
                    <div className="mt-1 p-2 bg-muted rounded font-mono text-xs">
                      quantquack dataset sync run {currentDataset.id}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {currentDataset.metadata && (
            <Card>
              <CardHeader>
                <CardTitle>{t('datasets.metadata')}</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs bg-muted p-4 rounded overflow-auto">
                  {JSON.stringify(currentDataset.metadata, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
            <DialogDescription>
              {t('datasets.deleteConfirmation', { name: currentDataset.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? t('common.loading') : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('datasets.regenerateColumns')}</DialogTitle>
            <DialogDescription>
              {t('datasets.regenerateColumnsConfirmation', { name: currentDataset.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegenerateDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleRegenerate} disabled={isRegenerating}>
              {isRegenerating ? t('common.loading') : t('datasets.regenerate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
